import React, { forwardRef, useContext, useState, useEffect, useRef } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled } from "@mui/system";
import { visuallyHidden } from "@mui/utils";
import { TableVirtuoso } from "react-virtuoso";
import Paper from "@mui/material/Paper";
import DynamicHtml from "../CommonComponent/DynamicHtml";
import DynamicCell from "../CommonComponent/DynamicCell";
import DisplayPrescriberNotes from "./DisplayPrescriberNotes";
import DisplayPAForms from "./DisplayPAForms";
import PrescriberNotesModal from "../PrescriberNote/PrescriberNotesModal";
import { ConfigurationContext } from "../../../contexts/Configuration";
import { CommonService } from "@services";
import { DynamicDataService } from "@services";
import DesktopSortIcon from "../CommonComponent/DesktopSortIcon";

const DisplayCoverage = forwardRef(
  (
    {
      dynamicColumns,
      tableRows,
      orderBy,
      order,
      onRequestSort,
      prescriberCustomNotes,
      stateId,
      noDataWarning,
      showDataSource,
      channelFilter,
    },
    ref
  ) => {
    const matchesMobileWidth = useMediaQuery("(max-width:1032px)");
    const { appliedConfiguration } = useContext(ConfigurationContext);
    const CUSTOM_VALUE_DATAFIELD = "_dataFieldsWithCustomValue";
    const CONTINGENT_ICON_DATAFIELD = "CoverageContingentIcon";
    const [open, setOpen] = useState(false);
    const [prescriberNoteResponse, setPrescriberNoteResponse] = useState(null);
    const [additionalPrescriberNoteCriteria, setAdditionalPrescriberNoteCriteria] = useState(null);
    const [contingentDataField, setContingentDataField] = useState(null);
    const [activeRow, setActiveRow] = useState(null);
    const [currentPlanName, setCurrentPlanName] = useState(null);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [customNotePopupOpen, setCustomNotePopupOpen] = useState(false);
    const [customNoteData, setCustomNoteData] = useState(null);
    const [scrollPosY, setScrollPosY] = useState(0);
    const tableContainerRef = useRef(null);
    const capturedScrollPositionsRef = useRef({
      tableTop: 0,
      tableLeft: 0,
    });

    const coverageTableHeadRef = useRef({});
    const benefitTypeId = dynamicColumns?.[0]?.benefitTypeId;
    const createSortHandler = (property) => (event) => {
      coverageTableHeadRef.current[property] = event?.currentTarget;

      onRequestSort(event, property);
      if (ref?.current) {
        ref.current.scrollTo(0, 0);
      }
    };

    useEffect(() => {
      if (coverageTableHeadRef?.current[orderBy]) {
        setTimeout(() => {
          coverageTableHeadRef?.current[orderBy]?.focus();
        }, 50);
      }
    }, [order, orderBy]);

    const isShowCardRow = (currentRow, dataField) => {
      if (dataField && dataField.toLowerCase() === "prescribernotes") {
        dataField = "PrescriberNotes_DisplayValue";
      }

      if (dataField && currentRow[dataField] !== null && currentRow[dataField] !== undefined && currentRow[dataField].trim() !== "") {
        return true;
      }
      else {
        return false;
      }
    };
    
    const captureScrollPositions = () => {
      if (tableContainerRef?.current) {
        capturedScrollPositionsRef.current.tableTop = tableContainerRef.current.scrollTop;
        capturedScrollPositionsRef.current.tableLeft = tableContainerRef.current.scrollLeft;
      }
    };

    const handlePopupClose = () => {
      document.body.classList.remove("no-scroll");
      ref?.current?.classList.remove("disable-scroll");
      setTimeout(() => {
        restoreScrollPositions();
      }, 1);
    };

    const restoreScrollPositions = () => {
      if (!matchesMobileWidth && tableContainerRef?.current && capturedScrollPositionsRef?.current) {
        tableContainerRef.current.scrollTop = capturedScrollPositionsRef.current.tableTop;
        tableContainerRef.current.scrollLeft = capturedScrollPositionsRef.current.tableLeft;
      }
    };

    const handlePopupOpen = () => {
      if (matchesMobileWidth) {
        ref?.current?.classList.add("disable-scroll");
      }
    };

    const togglePopupOpen = (toggleOpen) => {
      adjustBodyScrollPosition(toggleOpen, false);
      setOpen(toggleOpen);
    };

    const toggleCustomNotePopupOpen = (toggleOpen) => {
      adjustBodyScrollPosition(toggleOpen, true);
      setCustomNotePopupOpen(toggleOpen);
    };

    const adjustBodyScrollPosition =(toggleOpen, isCustom) => {
      //This is to fix an issue on iPhone browsers where a gap shows at the bottom of the screen that the modal background overlay does not cover
      if(toggleOpen) {
        const scrollY = window.scrollY || window.pageYOffset;
        setScrollPosY(scrollY);
        document.body.style.top = `-${scrollY}px`;
        document.body.style.position = "fixed";
        // Force reflow
        void document.body.offsetHeight;
      }
      else if (isCustom && customNotePopupOpen) {
        document.body.style.position = "";
        document.body.style.top = "";
        // Restore scroll
        window.scrollTo(0, scrollPosY);
      }
      else if (!isCustom && open) {
        document.body.style.position = "";
        document.body.style.top = "";
        // Restore scroll
        window.scrollTo(0, scrollPosY);
      }
    };

    const StyledTableRow = styled(TableRow)(() => ({
      "&:nth-of-type(odd)": {
        backgroundColor: "#EFF6F9",
      },
      "&:nth-of-type(even)": {
        backgroundColor: "#ffffff",
      },
    }));

    const getNoUICustomValue = function (headCell, currentRow) {
      const customValueDataFields = currentRow[CUSTOM_VALUE_DATAFIELD];
      const isCustomValue =
        customValueDataFields?.split(",")?.findIndex((item) => item === headCell.dataField.toLowerCase()) > -1;
      let dataValue = currentRow[headCell.dataField];

      //Get coverageContingentIcon for status column if configured
      let coverageContingentIcon = "";
      if(headCell.isStatusColumn && Object.hasOwn(currentRow, CONTINGENT_ICON_DATAFIELD)) {
        coverageContingentIcon = currentRow[CONTINGENT_ICON_DATAFIELD];
      }

      return renderRowData(dataValue, headCell, isCustomValue, coverageContingentIcon);
    };

    const renderRowData = function (dataValue, headCell, isCustomValue, coverageContingentIcon) {
      if (headCell.coverageContingentData) {
        const { rowValue, iconPath, displayPrefix, altText } = DynamicDataService.extractDynamicRowData(dataValue);
        if (isCustomValue) return renderCustomValue(rowValue);

        return <DynamicCell rowValue={rowValue} iconPath={iconPath} displayPrefix={displayPrefix} altText={altText} />;
      }
      else if (headCell.isStatusColumn && headCell.coverageContingentIcon && coverageContingentIcon) {
        const { rowValue, iconPath, altText } = DynamicDataService.extractDynamicRowData(coverageContingentIcon);
        const contingentFieldSetup = appliedConfiguration.coverageContingentDataFieldSetUp[0];
        const iconDisplayPosition = contingentFieldSetup?.iconDisplayPosition;

        return <DynamicCell rowValue={rowValue} iconPath={iconPath} displayPrefix={dataValue} altText={altText} iconDisplayPosition={iconDisplayPosition} />;
      }
      //If not using contingent data
      return isCustomValue ? renderCustomValue(dataValue) : dataValue;
    };

    function renderCustomValue(value) {
      return (
        <div
          className={showDataSource ? "norstella-cf-dataSource" : "norstella-cf-customValue"}
          dangerouslySetInnerHTML={{ __html: CommonService.sanitizeHTML(CommonService.htmlDecode(value ?? "")) }}
        />
      );
    }
    const VirtuosoTableScroller = React.forwardRef((props, ref) => {
      React.useImperativeHandle(ref, () => tableContainerRef.current);

      return (
        <TableContainer
          component={Paper}
          {...props}
          ref={tableContainerRef}
          sx={{ height: 450 }}
          className="norstella-cf-coverageTableContainer"
          id="coverageTableContainer"
        />
      );
    });

    VirtuosoTableScroller.displayName = "VirtuosoTableScroller";

    const VirtuosoTableBody = React.forwardRef((props, ref) => (
      <>
        {noDataWarning != "" && (
          <TableRow className="norstella-cf-trNoResult" part="cfTrNoResult">
            <TableCell colSpan={dynamicColumns.length} part="cfTcNoResult" className="norstella-cf-tcNoResult">
              <span className="norstella-cf-noresultmessage" part="cfNoResultmessage">
                {noDataWarning}
              </span>
            </TableCell>
          </TableRow>
        )}
        <TableBody
          {...props}
          part="cfDisplayCoverageTableBody"
          className="norstella-cf-displayCoverageTableBody"
          ref={ref}
        />
      </>
    ));
    VirtuosoTableBody.displayName = "VirtuosoTableBody";

    const VirtuosoTableComponents = {
      Scroller: VirtuosoTableScroller,
      Table: (props) => (
        <Table
          {...props}
          sx={{ borderCollapse: "separate", tableLayout: "fixed" }}
          className="norstella-cf-displayCoverageTable"
          stickyHeader
          aria-label="coverage table"
          part="cfDisplayCoverageTable"
        />
      ),
      TableHead,
      // eslint-disable-next-line no-unused-vars
      TableRow: ({ item: _item, ...props }) => (
        <StyledTableRow {...props} sx={{ "&:last-child td, &:last-child th": { border: 0 } }} />
      ),
      TableBody: VirtuosoTableBody,
    };

    const getColumnDataField = (dataField) => {
      var dataFieldClassName = dataField.includes("CoverageContingentData") ? "CoverageContingentData" : dataField;
      const showAllTabName = appliedConfiguration?.coverageFinderChannelAdditionalDetails?.showAllTabName ?? "Show All";
      return channelFilter === showAllTabName ? dataFieldClassName + "_showAll" : dataFieldClassName;
    };

    const FixedHeaderContent = React.memo(() => (
      <TableRow className="norstella-cf-tableHeaderRow norstella-cf-focusHeadRow" part="cfTableHeaderRow">
        {dynamicColumns.map((headCell, index) => (
          <TableCell
            key={headCell.dataField}
            part="cfTableHeaderCell"
            sortDirection={orderBy === headCell.dataField && order !== "" ? order : false}
            className={`norstella-cf-tableHeaderCell ${headCell.dataField === "PlanName" && dynamicColumns.length > 3
              ? "norstella-cf-tableHeaderCellPlanNamePAR"
              : `norstella-cf-tableHeaderCell${index}`
              } 
                norstella-cf-tableHeaderCell-${getColumnDataField(headCell.dataField)}
              `}
          >
            <TableSortLabel
              ref={(el) => (coverageTableHeadRef.current[headCell.dataField] = el)}
              tabIndex="0"
              active={orderBy === headCell.dataField && order !== ""}
              direction={orderBy === headCell.dataField && order !== "" ? order : "asc"}
              onClick={createSortHandler(headCell.dataField)}
              className={`norstella-cf-tableSortLabel norstella-cf-tableSortLabel${index} norstella-cf-focusCellData`}
              part="cfTableHeaderRowCellSort"
              IconComponent={() => <DesktopSortIcon benefitTypeId={benefitTypeId} direction={(orderBy === headCell.dataField && order !== "" ? order : "")} />}
              hideSortIcon={false}
            >
              <DynamicHtml
                input={headCell.columnTitle}
                part="cfDivCoverageDetail"
                type="label"
                className={`norstella-cf-columnTitle norstella-cf-columnTitle${index} norstella-cf-columnTitle-${getColumnDataField(headCell.dataField)}`}
              />
              {orderBy === headCell.dataField ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    ));
    FixedHeaderContent.displayName = "FixedHeaderContent";

    function getCustomIconPath(headCell) {
      if (appliedConfiguration && headCell) {
        return appliedConfiguration?.coverageFinderGridColumnIcons?.find(
          (item) =>
            item.coverageFinderColumnId === headCell.coverageFinderColumnId &&
            item.benefitTypeId === headCell.benefitTypeId &&
            item.columnDataField === headCell.dataField &&
            item.gridType == "gridSetting"
        )?.iconPath;
      }
      return null;
    }

    function rowContent(_index, row) {
      return (
        <>
          {dynamicColumns.map((headCell, headerindex) => (
            <TableCell
              key={headCell.dataField}
              part="cfTableDataCell"
              className={`norstella-cf-tableDataCell norstella-cf-tc${headerindex} norstella-cf-tableBodyCell-focus`}
              component={headerindex === 0 ? "th" : "td"}
              tabIndex={row["PrescriberNotes_DisplayValue"] === "display icon" ? (!headCell.dataField.match("PrescriberNotes") ? 0 : -1) : -1}
            >
              {appliedConfiguration?.coverageFinderGridColumnIcons?.find(
                (item) =>
                  item.coverageFinderColumnId === headCell.coverageFinderColumnId &&
                  item.benefitTypeId === headCell.benefitTypeId &&
                  item.dataElement === row[headCell.dataField] &&
                  item.gridType == "gridSetting"
              ) ? (
                <img
                  part="cfDisplayRowCellIcon"
                  className="norstella-cf-DynamicColCellIcon"
                  src={
                    appliedConfiguration?.coverageFinderGridColumnIcons?.find(
                      (item) =>
                        item.coverageFinderColumnId === headCell.coverageFinderColumnId &&
                        item.benefitTypeId === headCell.benefitTypeId &&
                        item.dataElement === row[headCell.dataField] &&
                        item.gridType == "gridSetting"
                    ).iconPath
                  }
                  alt={
                    appliedConfiguration?.coverageFinderGridColumnIcons?.find(
                      (item) =>
                        item.coverageFinderColumnId === headCell.coverageFinderColumnId &&
                        item.benefitTypeId === headCell.benefitTypeId &&
                        item.dataElement === row[headCell.dataField] &&
                        item.gridType == "gridSetting"
                    ).altText ?? "Icon"
                  }
                />
              ) : (
                getNoUICustomValue(
                  headCell,
                  row
                )
              )}
              {/* PA Form Icon */}
              {headCell.dataField.match("PAFormIcon") ? (
                <DisplayPAForms row={row} customIconPath={getCustomIconPath(headCell)} />
              ) : (
                ""
              )}
              {headCell.dataField.match("PrescriberNotes") ? (
                <DisplayPrescriberNotes
                  row={row}
                  dynamicColumns={dynamicColumns}
                  prescriberCustomNotes={prescriberCustomNotes}
                  stateId={stateId}
                  cellIcon={appliedConfiguration?.coverageFinderGridColumnIcons?.find(
                    (item) =>
                      item.coverageFinderColumnId === headCell.coverageFinderColumnId &&
                      item.benefitTypeId === headCell.benefitTypeId &&
                      item.gridType == "gridSetting"
                  )}
                  setOpen={togglePopupOpen}
                  setPrescriberNoteResponse={setPrescriberNoteResponse}
                  setAdditionalPrescriberNoteCriteria={setAdditionalPrescriberNoteCriteria}
                  setContingentDataField={setContingentDataField}
                  setActiveRow={setActiveRow}
                  setActivePlanName={setCurrentPlanName}
                  setIsLoadingPage={setIsLoadingPage}
                  setCustomNoteData={setCustomNoteData}
                  setCustomNotePopupOpen={toggleCustomNotePopupOpen}
                  captureScrollPositions={captureScrollPositions}
                  showDataSource={showDataSource}
                />
              ) : (
                ""
              )}
            </TableCell>
          ))}
        </>
      );
    }

    return (
      <>
        <div
          part="cfCoverage-content"
          className="norstella-cf-coverage-content"
          style={{ position: "relative" }}
          id="coverage-content"
        >
          <div className="norstella-cf-flexAlignResponsive" part="cfDivDisplayCoverage">
            <div aria-live="polite" aria-atomic="true" className="norstella-cf-visiblehidden-aria-label">
              {noDataWarning}
            </div>
            {!matchesMobileWidth && (
              <Paper
                part="cfPprTableCoverage"
                style={{ height: 450, width: "100%" }}
                className="norstella-cf-pprTableCoverage norstella-cf-focusHeadContainer"
              >
                <TableVirtuoso
                  data={tableRows}
                  components={VirtuosoTableComponents}
                  fixedHeaderContent={() => <FixedHeaderContent />}
                  itemContent={rowContent}
                />
              </Paper>
            )}
            {matchesMobileWidth && dynamicColumns && dynamicColumns.length > 0 && (
              <div id="mobileCardView" part="cfMobileCardView" className="norstella-cf-mobileCardView" ref={ref}>
                {noDataWarning != "" && (
                  <span className="norstella-cf-noresultmessage" part="cfNoResultmessage">
                    {noDataWarning}
                  </span>
                )}
                {tableRows?.map((row, i) => {
                  return (
                    <Card key={i} className="norstella-cf-card" part="cfCard" variant="outlined">
                      <CardContent
                        part="cfDisplayCoverageCardContent"
                        className="norstella-cf-displayCoverageCardContent">
                        <div part="cfSpansContainer" className="norstella-cf-spansContainer">
                          {dynamicColumns.map((headCell, headerindex) => {
                            const cellIcon = appliedConfiguration?.coverageFinderGridColumnIcons?.find(
                              (item) =>
                                item.coverageFinderColumnId === headCell.coverageFinderColumnId &&
                                item.benefitTypeId === headCell.benefitTypeId &&
                                item.dataElement === row[headCell.dataField] &&
                                item.gridType == "gridSetting"
                            );
                            return headerindex === 0 ? (
                              // This will be the first field on the card. Font style will be bold and slightly larger by default. Usually the plan name/row header
                              <h4 key={headerindex} part="cfSpanPlanHeader" className="norstella-cf-mobilePlanHeader">
                                {cellIcon ? (
                                  <img
                                    part="cfDisplayRowCellIcon"
                                    className="norstella-cf-DynamicColCellIcon"
                                    src={cellIcon?.iconPath}
                                    alt={cellIcon?.altText ?? "Icon"} />
                                ) : (
                                  getNoUICustomValue(
                                    headCell,
                                    row
                                  )
                                )}
                              </h4>
                            ) : (isShowCardRow(row, headCell.dataField) &&  (
                              <div
                                key={headCell.dataField}
                                part="cfSpanCoverageDetail"
                                className="norstella-cf-mobilePlanDetails">
                                {/* Status */}
                                {(headCell.dataField === "Status" ||
                                  headCell.columnTitle === "Coverage" ||
                                  headCell.isStatusColumn) && (
                                    <span part="cfMobileCoverageStatus" className="norstella-cf-mobileCoverageStatus">
                                      <DynamicHtml
                                        input={headCell.columnTitle}
                                        part="cfDivCoverageDetail"
                                        className="norstella-cf-mobileCoverageDetail"
                                        type="label" />
                                      :{" "}
                                      {cellIcon ? (
                                        <img
                                          part="cfDisplayRowCellIcon"
                                          className="norstella-cf-DynamicColCellIcon"
                                          src={cellIcon?.iconPath}
                                          alt={cellIcon?.altText ?? "Icon"} />
                                      ) : (
                                        getNoUICustomValue(
                                          headCell,
                                          row
                                        )
                                      )}
                                    </span>
                                  )}
                                {/* All fields other than prescriber note and status */}
                                {headCell.dataField !== "Status" &&
                                  headCell.columnTitle !== "Coverage" &&
                                  !headCell.isStatusColumn &&
                                  headCell.dataField !== "PrescriberNotes" &&
                                  (
                                    <span part="cfSpanCoverageDetail" className="norstella-cf-mobilePlanDetails">
                                      <DynamicHtml
                                        input={headCell.columnTitle}
                                        part="cfDivCoverageDetail"
                                        className="norstella-cf-mobileCoverageDetail"
                                        type="label" />
                                      :{" "}
                                      {cellIcon ? (
                                        <img
                                          part="cfDisplayRowCellIcon"
                                          className="norstella-cf-DynamicColCellIcon"
                                          src={cellIcon?.iconPath}
                                          alt={cellIcon?.altText ?? "Icon"} />
                                      ) : (
                                        getNoUICustomValue(
                                          headCell,
                                          row
                                        )
                                      )}
                                    </span>
                                  )}
                                {/* Prescriber Note */}
                                {headCell.dataField.match("PrescriberNotes") ? (
                                  <span part="cfMobilePrescriberNotes" className="norstella-cf-mobilePrescriberNotes">
                                    <DynamicHtml
                                      input={headCell.columnTitle}
                                      part="cfDivCoverageDetail"
                                      className="norstella-cf-mobileCoverageDetail"
                                      type="label" />:{" "}
                                    <DisplayPrescriberNotes
                                      row={row}
                                      dynamicColumns={dynamicColumns}
                                      prescriberCustomNotes={prescriberCustomNotes}
                                      stateId={stateId}
                                      cellIcon={appliedConfiguration?.coverageFinderGridColumnIcons?.find(
                                        (item) =>
                                          item.coverageFinderColumnId === headCell.coverageFinderColumnId &&
                                          item.benefitTypeId === headCell.benefitTypeId &&
                                          item.gridType == "gridSetting"
                                      )}
                                      setOpen={togglePopupOpen}
                                      setPrescriberNoteResponse={setPrescriberNoteResponse}
                                      setAdditionalPrescriberNoteCriteria={setAdditionalPrescriberNoteCriteria}
                                      setContingentDataField={setContingentDataField}
                                      setActiveRow={setActiveRow}
                                      setActivePlanName={setCurrentPlanName}
                                      setIsLoadingPage={setIsLoadingPage}
                                      setCustomNoteData={setCustomNoteData}
                                      setCustomNotePopupOpen={toggleCustomNotePopupOpen}
                                      captureScrollPositions={captureScrollPositions} />
                                  </span>
                                ) : (
                                  ""
                                )}
                                {/* PA Form Icon */}
                                {headCell.dataField.match("PAFormIcon") ? (
                                  <DisplayPAForms row={row} customIconPath={getCustomIconPath(headCell)} />
                                ) : (
                                  ""
                                )}
                              </div>
                            ));
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ display: "none" }} id="hdnPlanNames">
            {/* This div contains all plan/controller names to help with automated testing */}
            {tableRows.map((row) => (row["PlanName"] ? row["PlanName"] : row["ControllerName"])).join("|")}
          </div>
        </div>
        <PrescriberNotesModal
          isOpen={open}
          customNotePopupOpen={customNotePopupOpen}
          isLoadingPage={isLoadingPage}
          prescriberNoteResponse={prescriberNoteResponse}
          additionalPrescriberNoteCriteria={additionalPrescriberNoteCriteria}
          currentPlanName={currentPlanName}
          activeRow={activeRow}
          contingentDataField={contingentDataField}
          customNoteData={customNoteData}
          setOpen={togglePopupOpen}
          setCustomNotePopupOpen={toggleCustomNotePopupOpen}
          handlePopupClose={handlePopupClose}
          handlePopupOpen={handlePopupOpen}
        />
      </>
    );
  }
);

DisplayCoverage.displayName = "DisplayCoverage";

export default DisplayCoverage;
